import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import PageHeader from '../../components/UI/PageHeader';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import FormSelect from '../../components/Forms/FormSelect';
import FormInput from '../../components/Forms/FormInput';
import Pagination from '../../components/UI/Pagination';
import { getUserCharges } from '../../services/payments/paymentsService';
import { ChargeDto, ChargeStatus } from '../../services/payments/types';

const ChargesList: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();

  const [charges, setCharges] = useState<ChargeDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [userIdFilter, setUserIdFilter] = useState<string>('');
  
  // Obter userId da query string, se fornecido
  const queryParams = new URLSearchParams(location.search);
  const userId = queryParams.get('user_id') || '';

  useEffect(() => {
    if (userId) {
      setUserIdFilter(userId);
    }
  }, [userId]);

  const fetchCharges = useCallback(async () => {
    if (!auth.isAuthenticated) {
      setError('You must be authenticated to view charges.');
      return;
    }

    if (!userIdFilter) {
      setError('Please specify a User ID to view charges.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await getUserCharges(auth.user || null, userIdFilter);
      
      // Filtrar por status se necessário
      const filteredData = statusFilter 
        ? data.filter(charge => charge.charge_status === statusFilter)
        : data;
      
      setCharges(filteredData);
      
      // Estimar total de páginas (normalmente viria do backend)
      const pageSize = 10;
      setTotalPages(Math.max(1, Math.ceil(filteredData.length / pageSize)));
    } catch (err) {
      console.error('Error fetching charges:', err);
      setError('Failed to fetch charges. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [auth.isAuthenticated, auth.user, userIdFilter, statusFilter]);

  useEffect(() => {
    fetchCharges();
  }, [fetchCharges]);

  const handleUserIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserIdFilter(e.target.value);
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1); // Reset para primeira página ao mudar filtro
  };

  const handleSubmitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchCharges();
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Atualizar URL com parâmetros de paginação
    const params = new URLSearchParams(location.search);
    params.set('page', page.toString());
    if (userIdFilter) params.set('user_id', userIdFilter);
    if (statusFilter) params.set('status', statusFilter);
    navigate({
      pathname: location.pathname,
      search: params.toString()
    });
  };

  const handleCreateCharge = () => {
    navigate('/payments/create');
  };

  // Formatar valores monetários
  const formatCurrency = (amount?: number, currency: string = 'BRL') => {
    if (amount === undefined) return 'N/A';
    
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency || 'BRL'
    }).format(amount / 100); // Assumindo que o valor está em centavos
  };

  // Formatar data para o formato local
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  // Obter classes de CSS para o status
  const getStatusBadgeClasses = (status?: ChargeStatus) => {
    switch (status) {
      case ChargeStatus.COMPLETED:
        return 'bg-green-100 text-green-800';
      case ChargeStatus.PENDING:
        return 'bg-yellow-100 text-yellow-800';
      case ChargeStatus.CANCELLED:
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div>
      <PageHeader
        title="Charges"
        breadcrumbs={[
          { label: "Dashboard", to: "/" },
          { label: "Charges" }
        ]}
        actionButton={{
          label: "Create Charge",
          icon: "plus",
          onClick: handleCreateCharge
        }}
      />

      {error && (
        <div className="bg-red-50 text-red-700 p-4 rounded-md mb-4">
          {error}
          <Button 
            variant="ghost" 
            size="sm" 
            className="ml-2"
            onClick={fetchCharges}
          >
            Retry
          </Button>
        </div>
      )}

      <Card>
        <form onSubmit={handleSubmitSearch} className="mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormInput
              label="User ID"
              id="userId"
              name="userId"
              value={userIdFilter}
              onChange={handleUserIdChange}
              placeholder="Filter by User ID"
              leftIcon={<i className="fas fa-user text-gray-400"></i>}
            />

            <FormSelect
              label="Status"
              id="statusFilter"
              value={statusFilter}
              onChange={handleStatusChange}
              leftIcon={<i className="fas fa-filter text-gray-400"></i>}
              options={[
                { value: "", label: "All Statuses" },
                { value: ChargeStatus.PENDING, label: "Pending" },
                { value: ChargeStatus.COMPLETED, label: "Completed" },
                { value: ChargeStatus.CANCELLED, label: "Cancelled" },
              ]}
            />
          </div>

          <div className="flex justify-end">
            <Button
              type="submit"
              variant="primary"
              leftIcon={<i className="fas fa-search mr-2"></i>}
              className="w-full sm:w-auto"
            >
              Search
            </Button>
          </div>
        </form>
        
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : charges.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">
              No charges found
              {userIdFilter && ` for User ID: ${userIdFilter}`}
              {statusFilter && ` with status: ${statusFilter}`}.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Due Date
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Paid At
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {charges.map((charge) => (
                  <tr key={charge.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      {charge.description || 'N/A'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {formatCurrency(charge.amount, charge.currency)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClasses(charge.charge_status)}`}>
                        {charge.charge_status || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {formatDate(charge.due_date)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {formatDate(charge.paid_at)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="flex space-x-1">
                        <Link to={`/payments/charges/${charge.id}`}>
                          <Button variant="ghost" size="xs" title="View details">
                            <i className="fas fa-eye"></i>
                          </Button>
                        </Link>
                        <Link to={`/payments/charges/${charge.id}/payments`}>
                          <Button variant="ghost" size="xs" title="View payments">
                            <i className="fas fa-credit-card"></i>
                          </Button>
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              preserveParams={true}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default ChargesList;